import React, { useMemo, useRef } from "react";
import ResourceManager, { useResourceList } from "../../components/admin/ResourceManager";
import { fmtDate } from "../../lib/resources";
import { Button } from "../../components/ui/button";
import { Eye } from "lucide-react";

export default function ManageChapters() {
  const [classes] = useResourceList("classes");
  const [courses] = useResourceList("courses");
  const [subjects] = useResourceList("subjects");
  const classOpts = useMemo(() => classes.map((c) => ({ value: c.name, label: c.name })), [classes]);
  const courseOpts = useMemo(() => courses.map((c) => ({ value: c.name, label: c.name })), [courses]);
  const prevClassRef = useRef("");
  const prevCourseRef = useRef("");
  const prevSubjectRef = useRef("");

  const dynamicFields = useMemo(() => (fields, form) => {
    const cls = form?.class_name || "";
    const course = form?.course_name || "";
    const subj = form?.subject_name || "";
    const filteredSubjectOpts = course && cls
      ? subjects.filter((s) => {
          const sameCourse = (Array.isArray(s.course_names) && s.course_names.includes(course)) || s.course_name === course;
          const sameClass  = (Array.isArray(s.class_names) && s.class_names.includes(cls)) || s.class_name === cls;
          return sameCourse && sameClass;
        }).map((s) => ({ value: s.name, label: s.name }))
      : [];
    return fields.map((f) => {
      if (f.key === "subject_name") {
        const merged = [...filteredSubjectOpts];
        if (subj && !merged.some((o) => o.value === subj)) {
          merged.push({ value: subj, label: subj });
        }
        return { ...f, options: merged, disabled: !course || !cls };
      }
      return f;
    });
  }, [subjects]);

  const onFormChange = (form, setForm) => {
    const cls = form?.class_name || "";
    const course = form?.course_name || "";
    const subj = form?.subject_name || "";
    if ((prevClassRef.current !== cls && prevClassRef.current !== "") ||
        (prevCourseRef.current !== course && prevCourseRef.current !== "")) {
      setForm?.((s) => ({ ...s, subject_name: "" }));
    }
    prevSubjectRef.current = subj;
    prevClassRef.current = cls;
    prevCourseRef.current = course;
  };

  const extraActions = (row) => {
    return (
      <Button 
        size="sm" 
        variant="outline" 
        onClick={() => {
          if (row.content) {
            if (row.content.startsWith("[Data URL stored")) {
              alert("This file is stored in an old format and is too large to view directly. Please click 'Edit' and re-upload it.");
            } else {
              window.open(row.content, "_blank");
            }
          }
        }} 
        disabled={!row.content}
        className="h-8 bg-white/5 border-white/10 text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-50"
        title={row.content ? "View Resource File" : "No file attached"}
      >
        <Eye size={12} className="mr-1" />View
      </Button>
    );
  };

  return (
    <ResourceManager
      kind="chapters"
      selectable={true}
      eyebrow="Manage Resources"
      title="Resources"
      subtitle="Resources connected to class, course and chapter."
      excelHint="Columns: name, class_name, course_name, subject_name"
      fields={[
        { key: "class_name", label: "Class", type: "select", options: classOpts, placeholder: "Select class", required: true },
        { key: "course_name", label: "Course", type: "select", options: courseOpts, placeholder: "Select course", required: true },
        { key: "subject_name", label: "Chapter", type: "select", options: [], placeholder: "Select chapter", required: true },
        { key: "name", label: "Resource Name", type: "text", required: true, placeholder: "e.g. Loops & Conditions" },
        { key: "content", label: "Resource Content (PDF, PPT, DOC, video, image, zip)", type: "file", accept: ".pdf,.ppt,.pptx,.doc,.docx,.zip,image/*,video/*", span: 2 },
      ]}
      columns={[
        { key: "class_name", label: "Class" },
        { key: "course_name", label: "Course" },
        { key: "subject_name", label: "Chapter" },
        { key: "name", label: "Resource" },
        { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
      ]}
      dynamicFields={dynamicFields}
      onFormChange={onFormChange}
      extraActions={extraActions}
    />
  );
}
